// Sold-out cart lines: the swap button, and removing a sold-out line.
// Markup and the rules for which variant is offered: snippets/cart-sold-out-line.liquid.
//
// Shopify quirks this works around (verified 22 Oct 2026, UPGRADE-NOTES.md
// "A sold-out variant can sit in the cart"):
// - /cart/change.js refuses a sold-out line, by line number or line key
//   (422 "already sold out"). Dawn's remove button uses it, so on its own it
//   cannot remove these lines. /cart/update.js by line key can.
// - Any accepted cart change drops every sold-out line, so the page must
//   re-render from the response rather than assume what is left.

// POST /cart/update.js and re-render the cart sections, the way Dawn's
// cart.js does after /cart/change.js.
async function cartSoldOutUpdate(cartItems, body) {
  const sections = cartItems.getSectionsToRender();
  const response = await fetch(`${routes.cart_update_url}.js`, {
    ...fetchConfig(),
    body: JSON.stringify({
      ...body,
      sections: sections.map((section) => section.section),
      sections_url: window.location.pathname,
    }),
  });
  const state = await response.json();
  if (state.status || !state.sections) throw new Error(state.description || state.message);

  sections.forEach((section) => {
    const container = document.getElementById(section.id);
    const target = container?.querySelector(section.selector) || container;
    const html = state.sections[section.section];
    if (target && html) target.innerHTML = cartItems.getSectionInnerHTML(html, section.selector);
  });
  cartItems.classList.toggle('is-empty', state.item_count === 0);
  document.getElementById('main-cart-footer')?.classList.toggle('is-empty', state.item_count === 0);
  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
  return state;
}

function cartSoldOutError(line, error) {
  const message = document.querySelector(`#Line-item-error-${line} .cart-item__error-text`);
  if (message) message.textContent = error.message || window.cartStrings.error;
}

if (!customElements.get('cart-swap-button')) {
  customElements.define(
    'cart-swap-button',
    class CartSwapButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.button?.addEventListener('click', () => this.swap());
      }

      // Add the new variant first, so a failed add leaves the cart untouched.
      // add.js already drops the sold-out lines; the update then removes any
      // that survive (by line key, read fresh: B2G1 splits one variant across
      // lines) and returns the re-rendered sections.
      async swap() {
        const cartItems = this.closest('cart-items');
        if (!cartItems || this.button.disabled) return;

        const { from, to, quantity, line } = this.dataset;
        this.button.disabled = true;
        cartItems.enableLoading(line);

        try {
          const added = await fetch(`${routes.cart_add_url}.js`, {
            ...fetchConfig(),
            body: JSON.stringify({ items: [{ id: Number(to), quantity: Number(quantity) }] }),
          }).then((response) => response.json());
          if (added.status) throw new Error(added.description || added.message);

          const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
          const updates = {};
          cart.items
            .filter((cartItem) => String(cartItem.variant_id) === from)
            .forEach((cartItem) => (updates[cartItem.key] = 0));
          await cartSoldOutUpdate(cartItems, { updates });
        } catch (error) {
          cartSoldOutError(line, error);
          this.button.disabled = false;
        } finally {
          cartItems.disableLoading(line);
        }
      }
    }
  );
}

// Remove button on a sold-out row: take over from Dawn's CartRemoveButton
// (capture phase, so its change.js call never runs) and remove the line by key.
// Rows without a sold-out card or tag keep Dawn's behaviour untouched.
if (!window.cartSoldOutRemoveBound) {
  window.cartSoldOutRemoveBound = true;
  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest('cart-remove-button');
      const row = button?.closest('.cart-item');
      if (!row || !row.querySelector('.cart-sold-out, .cart-sold-out__tag')) return;
      const key = row.querySelector('[data-quantity-line-key]')?.dataset.quantityLineKey;
      const cartItems = button.closest('cart-items');
      if (!key || !cartItems) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const line = button.dataset.index;
      cartItems.enableLoading(line);
      try {
        await cartSoldOutUpdate(cartItems, { updates: { [key]: 0 } });
      } catch (error) {
        cartSoldOutError(line, error);
      } finally {
        cartItems.disableLoading(line);
      }
    },
    true
  );
}
